import React, { useRef, useState, useEffect } from 'react';
import {
  Box, Button, Paper, Stack, Typography,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Chip,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  AttachFile as AttachFileIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { formatDate } from '../../utils/dateFormat';
import {
  useUploadAttendance,
  useDownloadAttendanceTemplate,
} from '../../hooks/useAttendance';
import { TABLE_PAPER_SX, HEAD_CELL_SX, BODY_CELL_SX, TABLE_ROW_SX } from '../common/tableStyles';

// ---- FORTHEYE soft-card tokens (shared with the rest of the portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';
const OUTLINED_BTN = { textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb' };
const GRADIENT_BTN = {
  textTransform: 'none', fontWeight: 700, borderRadius: '12px',
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};

// "2026-06-13" -> { label: "Sat 13 Jun", weekend: true }. Split the parts and
// build a LOCAL date (not `new Date(iso)`, which is parsed as UTC and can shift
// the weekday by a day) so Sat/Sun are always identified correctly.
const formatRowDate = (iso) => {
  if (!iso) return { label: '—', weekend: false };
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return { label: String(iso), weekend: false };
  const dt = new Date(y, m - 1, d);
  const weekday = dt.toLocaleDateString('en-US', { weekday: 'short' });
  const month = dt.toLocaleDateString('en-US', { month: 'short' });
  return {
    label: `${weekday} ${String(d).padStart(2, '0')} ${month}`,
    weekend: dt.getDay() === 0 || dt.getDay() === 6, // 0 = Sun, 6 = Sat
  };
};

// Soft summary tile (icon tile + number + caption).
const SummaryTile = ({ icon, value, label, color, tint }) => (
  <Paper
    elevation={0}
    sx={{
      ...CARD_SX,
      p: { xs: 1.75, md: 2 },
      flex: '1 1 160px',
      minWidth: 0,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ width: 38, height: 38, borderRadius: '11px', bgcolor: tint, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {React.cloneElement(icon, { sx: { fontSize: 20 } })}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color, lineHeight: 1.1 }}>{value}</Typography>
        <Typography sx={{ color: '#6b7280', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
      </Box>
    </Box>
  </Paper>
);

const AttendanceUpload = () => {
  const fileInputRef = useRef(null);
  const resultRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // Read the result straight off the mutation. This survives the re-renders that
  // the upload's invalidateQueries triggers — a per-mutate() onSuccess callback
  // can be skipped in those cases, which is why the result card "disappeared".
  const {
    mutate: upload,
    isPending: isUploading,   // v5 mutations expose isPending, not isLoading
    data: lastResult,
    reset: resetUpload,
  } = useUploadAttendance();
  const { mutate: downloadTemplate, isPending: isDownloadingTemplate } =
    useDownloadAttendanceTemplate();

  const handleFilePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(xlsx?|csv)$/i.test(file.name)) {
      toast.warn('Please select an .xlsx, .xls or .csv file.', {
        autoClose: 3000, position: 'top-right',
      });
      return;
    }
    resetUpload();       // drop any previous result card
    setSelectedFile(file);
  };

  const handleUpload = () => {
    if (!selectedFile) return;
    upload(selectedFile);
  };

  // Once a result lands, clear the picker and scroll the result into view.
  useEffect(() => {
    if (!lastResult) return;
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    const t = setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    return () => clearTimeout(t);
  }, [lastResult]);

  return (
    <Box>
      {/* Upload card */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.15rem', md: '1.35rem' }, color: '#111827', letterSpacing: '-0.01em' }}>
          Upload Daily Attendance
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5, mb: 2.5 }}>
          Upload a daily or monthly punch-in / punch-out file (.xlsx, .xls or
          .csv). Only new dates are added — dates already uploaded, or corrected
          by HR, are skipped and never overwritten. Days covered by an approved
          leave with no punch are skipped too.
        </Typography>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <Button
            variant="outlined"
            startIcon={isDownloadingTemplate ? <CircularProgress size={18} /> : <DownloadIcon />}
            onClick={() => downloadTemplate()}
            disabled={isDownloadingTemplate}
            sx={{ ...OUTLINED_BTN, py: 1.2, px: 3 }}
          >
            Download Template
          </Button>

          <Button
            variant="outlined"
            component="label"
            startIcon={<AttachFileIcon />}
            sx={{ ...OUTLINED_BTN, py: 1.2, px: 3 }}
          >
            {selectedFile ? selectedFile.name : 'Choose File (.xlsx / .xls / .csv)'}
            <input
              type="file"
              hidden
              ref={fileInputRef}
              accept=".xlsx,.xls,.csv"
              onChange={handleFilePick}
            />
          </Button>

          <Button
            variant="contained"
            disableElevation
            startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            sx={{ ...GRADIENT_BTN, py: 1.2, px: 4 }}
          >
            {isUploading ? 'Uploading…' : 'Upload Attendance'}
          </Button>
        </Stack>

        {/* File layout helper */}
        <Box sx={{ mt: 2.5, p: 2, bgcolor: '#f8fafc', border: '1px solid #eef0f4', borderRadius: '14px' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#111827', mb: 0.5 }}>
            File column layout (Excel or CSV — matched by header name)
          </Typography>
          <Typography variant="caption" sx={{ color: '#6b7280' }}>
            Serial No · Emp ID · Name · Shift · In Time · Out Time · Work Duration · OT · Total Duration · Status · Date
          </Typography>
        </Box>
      </Paper>

      {lastResult && (
        <Paper ref={resultRef} elevation={0} sx={CARD_SX}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, bgcolor: lastResult.error_count > 0 ? '#fffbeb' : '#f0fdf4', color: lastResult.error_count > 0 ? '#f59e0b' : '#16a34a' }}>
              {lastResult.error_count > 0 ? <WarningIcon /> : <CheckIcon />}
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.2rem' }, color: '#111827' }}>
              {lastResult.message}
            </Typography>
          </Stack>

          {/* Soft summary tiles */}
          <Stack direction="row" spacing={1.5} sx={{ mb: 2.5 }} flexWrap="wrap" useFlexGap>
            <SummaryTile
              icon={<CheckIcon />}
              value={lastResult.new_count ?? lastResult.success_count}
              label="New records processed"
              color="#16a34a"
              tint="#f0fdf4"
            />
            {lastResult.skipped_existing_count > 0 && (
              <SummaryTile
                icon={<CheckIcon />}
                value={lastResult.skipped_existing_count}
                label={lastResult.skipped_corrected_count > 0
                  ? `Skipped — already uploaded (${lastResult.skipped_corrected_count} HR correction${lastResult.skipped_corrected_count > 1 ? 's' : ''} kept)`
                  : 'Skipped — already uploaded'}
                color={ACCENT}
                tint="#eef4ff"
              />
            )}
            {lastResult.error_count > 0 && (
              <SummaryTile
                icon={<WarningIcon />}
                value={lastResult.error_count}
                label="Rows with errors"
                color="#ef4444"
                tint="#fef2f2"
              />
            )}
            {lastResult.date_range && (
              <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 1.75, md: 2 }, flex: '1 1 220px', minWidth: 0, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: '#6b7280', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {lastResult.date_range.min === lastResult.date_range.max ? 'Date saved' : 'Date range'}
                  </Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: ACCENT, lineHeight: 1.2 }}>
                    {lastResult.date_range.min === lastResult.date_range.max
                      ? formatDate(lastResult.date_range.min)
                      : `${formatDate(lastResult.date_range.min)} → ${formatDate(lastResult.date_range.max)}`}
                  </Typography>
                </Box>
              </Paper>
            )}
          </Stack>

          {/* All rows from the Excel — saved AND skipped, with every field. */}
          {lastResult.rows && lastResult.rows.length > 0 && (
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#111827', mb: 1.25 }}>
                All rows from the file ({lastResult.rows.length})
              </Typography>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ ...TABLE_PAPER_SX, borderRadius: '16px', maxHeight: 480 }}
              >
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    width: '100%',
                    tableLayout: 'auto',
                    // Tight padding so 12 columns fit without horizontal scroll
                    // and the inter-column gaps shrink.
                    '& th, & td': { whiteSpace: 'nowrap', px: 1, py: 0.75 },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {['Date', 'Emp ID', 'Name', 'Shift', 'In', 'Out', 'Work Dur.', 'OT', 'Tot. Dur.', 'Status', 'Result', 'Remarks'].map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            ...HEAD_CELL_SX,
                            // Opaque background + soft border so scrolled rows
                            // don't bleed through the sticky header.
                            backgroundColor: '#f8fafc',
                            borderBottom: '1px solid #eef0f4',
                            // Remarks soaks up the slack so the data columns hug
                            // their content (no big inter-column gaps).
                            width: h === 'Remarks' ? '100%' : '1%',
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lastResult.rows.map((r, i) => {
                      const skipped = r.outcome !== 'saved';
                      const s = String(r.status || '').toUpperCase();
                      const statusColor = s.includes('PRESENT') ? { bg: '#f0fdf4', fg: '#16a34a' }
                        : s.includes('ABSENT') ? { bg: '#fef2f2', fg: '#ef4444' }
                        : s.includes('HALF')   ? { bg: '#faf5ff', fg: '#9333ea' }
                        :                        { bg: '#f3f4f6', fg: '#6b7280' };
                      const dateInfo = formatRowDate(r.date);
                      return (
                        <TableRow key={i} sx={{ ...TABLE_ROW_SX, bgcolor: skipped ? '#fffdf7' : 'inherit' }}>
                          <TableCell sx={{ ...BODY_CELL_SX, whiteSpace: 'nowrap', fontWeight: 700, color: dateInfo.weekend ? '#b45309' : '#111827' }}>
                            {dateInfo.label}
                          </TableCell>
                          <TableCell sx={{ ...BODY_CELL_SX, fontFamily: 'monospace' }}>{r.emp_no}</TableCell>
                          <TableCell sx={{ ...BODY_CELL_SX, whiteSpace: 'nowrap' }}>{r.name}</TableCell>
                          <TableCell sx={BODY_CELL_SX}>{r.shift}</TableCell>
                          <TableCell sx={{ ...BODY_CELL_SX, fontFamily: 'monospace' }}>{r.in_time}</TableCell>
                          <TableCell sx={{ ...BODY_CELL_SX, fontFamily: 'monospace' }}>{r.out_time}</TableCell>
                          <TableCell sx={{ ...BODY_CELL_SX, fontFamily: 'monospace' }}>{r.work_duration}</TableCell>
                          <TableCell sx={{ ...BODY_CELL_SX, fontFamily: 'monospace' }}>{r.overtime}</TableCell>
                          <TableCell sx={{ ...BODY_CELL_SX, fontFamily: 'monospace' }}>{r.total_duration}</TableCell>
                          <TableCell sx={BODY_CELL_SX}>
                            <Chip size="small" label={r.status || '—'}
                              sx={{ fontWeight: 700, border: 'none', bgcolor: statusColor.bg, color: statusColor.fg }} />
                          </TableCell>
                          <TableCell sx={BODY_CELL_SX}>
                            <Chip size="small" label={skipped ? 'Skipped' : 'Saved'}
                              sx={{
                                fontWeight: 700, width: 'fit-content', border: 'none',
                                bgcolor: skipped ? '#fffbeb' : '#f0fdf4',
                                color: skipped ? '#f59e0b' : '#16a34a',
                              }} />
                          </TableCell>
                          <TableCell sx={{ ...BODY_CELL_SX, minWidth: 200, whiteSpace: 'normal !important' }}>
                            <Typography variant="body2" fontWeight={600}
                              color={skipped ? '#b45309' : '#16a34a'} sx={{ whiteSpace: 'nowrap' }}>
                              {r.remarks}
                            </Typography>
                            {skipped && r.reason && (
                              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                                {r.reason}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default AttendanceUpload;
